import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import { Box, InputAdornment, TextField, TextFieldProps } from "@mui/material";
import React from "react";

export type AuthTextFieldType =
  | "email"
  | "password"
  | "username"
  | "firstName"
  | "lastName"
  | "text";

interface AuthTextFieldProps extends Omit<TextFieldProps, "type"> {
  type?: AuthTextFieldType;
  showIcon?: boolean;
}

const iconMap: Record<AuthTextFieldType, React.ReactNode> = {
  email: <EmailIcon sx={{ fontSize: 22 }} />,
  password: <LockIcon sx={{ fontSize: 22 }} />,
  username: <PersonIcon sx={{ fontSize: 22 }} />,
  firstName: <PersonIcon sx={{ fontSize: 22 }} />,
  lastName: <PersonIcon sx={{ fontSize: 22 }} />,
  text: null,
};

const typeToInputType: Record<AuthTextFieldType, string> = {
  email: "email",
  password: "password",
  username: "text",
  firstName: "text",
  lastName: "text",
  text: "text",
};

const AuthTextField = React.forwardRef<HTMLDivElement, AuthTextFieldProps>(
  ({ type = "text", showIcon = true, sx, InputProps, ...props }, ref) => {
    const icon = showIcon ? iconMap[type] : null;
    const inputType = typeToInputType[type];

    return (
      <TextField
        {...props}
        ref={ref}
        type={inputType}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "16px",
            backgroundColor: "rgba(255,255,255,0.06)",
            color: "var(--ink)",
            "& fieldset": {
              borderColor: "var(--ink-faint)",
              borderWidth: 1.5,
            },
            "&:hover fieldset": {
              borderColor: "color-mix(in oklab, var(--hot) 55%, var(--ink-faint))",
            },
            "&.Mui-focused fieldset": {
              borderColor: "var(--hot)",
              borderWidth: 2,
            },
          },
          "& .MuiInputBase-input": {
            py: 1.25,
            fontSize: "1rem",
          },
          "& .MuiInputLabel-root": {
            color: "var(--ink-dim)",
            "&.Mui-focused": {
              color: "var(--hot)",
            },
          },
          "& .MuiFormHelperText-root": {
            color: "var(--ink-dim)",
          },
          ...sx,
        }}
        InputProps={{
          ...InputProps,
          startAdornment: icon ? (
            <InputAdornment position="start">
              <Box sx={{ color: "var(--hot)", display: "flex", alignItems: "center" }}>{icon}</Box>
            </InputAdornment>
          ) : (
            InputProps?.startAdornment
          ),
        }}
      />
    );
  }
);

AuthTextField.displayName = "AuthTextField";

export default AuthTextField;
